// Assessment utility functions and configurations

import { 
  ReadingAssessment, 
  ThinkingAssessment, 
  MathematicalAssessment,
  RadarChartDimension,
  AssessmentLevel,
  AssessmentScore,
  OCAssessmentResult
} from '../types/assessment';

// Assessment level definitions
export const ASSESSMENT_LEVELS: AssessmentLevel[] = [
  {
    min: 9,
    max: 10,
    label: 'Excellent',
    color: '#52c41a',
    description: 'Excellent level with innovative application abilities',
    ocImplication: 'Excellent chance for top-tier OC schools'
  },
  {
    min: 7,
    max: 8.9,
    label: 'Good',
    color: '#1890ff',
    description: 'Good mastery with proficient application skills',
    ocImplication: 'Meets requirements for most OC schools'
  },
  {
    min: 4,
    max: 6.9,
    label: 'Fair',
    color: '#faad14',
    description: 'Basic mastery requiring further development',
    ocImplication: 'Needs focused training and improvement'
  },
  {
    min: 1,
    max: 3.9,
    label: 'Needs Improvement',
    color: '#f5222d',
    description: 'Foundational skills need significant attention',
    ocImplication: 'Requires extensive practice and skill building'
  }
];

// Reading Assessment Dimensions
export const READING_DIMENSIONS: RadarChartDimension[] = [
  {
    key: 'literalComprehension',
    label: 'Literal Comprehension',
    description: '字面理解：直接从文本中提取明确信息的能力，包括事实识别、细节定位、词汇理解等。'
  },
  {
    key: 'inferentialUnderstanding',
    label: 'Inferential Understanding',
    description: '推理理解：基于文本信息进行逻辑推理和隐含意义理解，包括因果推理、预测分析等。'
  },
  {
    key: 'criticalAnalysis',
    label: 'Critical Analysis',
    description: '批判分析：对文本内容进行评价、分析和判断的能力，包括观点识别、论据评估等。'
  },
  {
    key: 'textStructureLanguage',
    label: 'Text Structure & Language',
    description: '文本结构与语言：理解文本组织方式和语言特色，包括体裁识别、修辞分析等。'
  },
  {
    key: 'synthesisConnection',
    label: 'Synthesis & Connection',
    description: '综合与联系：整合信息并建立内外部联系，包括主题概括、知识迁移等。'
  },
  {
    key: 'readingStrategies',
    label: 'Reading Strategies',
    description: '阅读策略：有效运用阅读技巧和策略，包括预测技巧、监控调整等。'
  }
];

// Thinking Skills Assessment Dimensions
export const THINKING_DIMENSIONS: RadarChartDimension[] = [
  {
    key: 'patternRecognition',
    label: 'Pattern Recognition',
    description: '模式识别：发现和理解各种模式规律的能力，包括数字序列、图形变化等。'
  },
  {
    key: 'logicalReasoning',
    label: 'Logical Reasoning',
    description: '逻辑推理：运用逻辑规则进行推理的能力，包括演绎推理、归纳推理等。'
  },
  {
    key: 'spatialReasoning',
    label: 'Spatial Reasoning',
    description: '空间推理：理解和操作空间关系的能力，包括3D图形理解、图形变换等。'
  },
  {
    key: 'abstractThinking',
    label: 'Abstract Thinking',
    description: '抽象思维：处理抽象概念和关系的能力，包括概念抽象化、符号操作等。'
  },
  {
    key: 'problemSolving',
    label: 'Problem Solving',
    description: '问题解决：系统性解决复杂问题的能力，包括问题分析、策略选择等。'
  },
  {
    key: 'creativeThinking',
    label: 'Creative Thinking',
    description: '创造性思维：产生新颖和独特解决方案的能力，包括发散思维、创新方法等。'
  }
];

// Mathematical Assessment Dimensions
export const MATHEMATICAL_DIMENSIONS: RadarChartDimension[] = [
  {
    key: 'numberAlgebra',
    label: 'Number & Algebra',
    description: '数与代数：数字概念、运算技能和早期代数思维，包括整数运算、分数小数等。'
  },
  {
    key: 'measurementGeometry',
    label: 'Measurement & Geometry',
    description: '测量与几何：空间概念、测量技能和几何理解，包括长度面积、图形性质等。'
  },
  {
    key: 'statisticsProbability',
    label: 'Statistics & Probability',
    description: '统计与概率：数据处理、统计分析和概率概念，包括图表制作、数据解读等。'
  },
  {
    key: 'problemSolving',
    label: 'Problem Solving',
    description: '问题解决：实际问题解决能力和策略运用，包括应用题、多步骤推理等。'
  },
  {
    key: 'mathematicalReasoning',
    label: 'Mathematical Reasoning',
    description: '数学推理：逻辑思维、推理能力和数学论证，包括模式发现、假设验证等。'
  },
  {
    key: 'communication',
    label: 'Communication',
    description: '数学交流：数学思维表达和交流能力，包括数学语言、解题过程表达等。'
  }
];

// Default radar chart colors
export const DEFAULT_RADAR_COLORS = {
  excellent: '#52c41a',
  good: '#1890ff', 
  fair: '#faad14',
  poor: '#f5222d',
  grid: '#d9d9d9',
  text: '#666666'
};

// Utility functions
export const getAssessmentLevel = (score: number): AssessmentLevel => {
  return ASSESSMENT_LEVELS.find(level => score >= level.min && score <= level.max) || ASSESSMENT_LEVELS[3];
};

export const extractDimensionScores = (assessment: any, dimensions: RadarChartDimension[]): AssessmentScore => {
  const scores: AssessmentScore = {};
  dimensions.forEach(dimension => {
    if (assessment[dimension.key] !== undefined) {
      scores[dimension.key] = assessment[dimension.key];
    }
  });
  return scores;
};

export const calculateOverallScore = (scores: Record<string, number>): number => {
  const values = Object.values(scores);
  return Math.round((values.reduce((sum, score) => sum + score, 0) / values.length) * 10) / 10;
};

export const getOCReadiness = (overallScore: number): OCAssessmentResult['overall']['ocReadiness'] => {
  if (overallScore >= 8.5) return 'Excellent';
  if (overallScore >= 7.0) return 'Good';
  if (overallScore >= 5.5) return 'Fair';
  return 'Needs Improvement';
};

export const generateRecommendations = (
  readingScore: number,
  thinkingScore: number,
  mathScore: number,
  detailedScores: {
    reading: ReadingAssessment;
    thinking: ThinkingAssessment;
    mathematics: MathematicalAssessment;
  }
): string[] => {
  const recommendations: string[] = [];
  
  // Overall module recommendations
  if (readingScore < 7) {
    recommendations.push('加强阅读理解训练，多读各类文体提升理解能力');
  }
  if (thinkingScore < 7) {
    recommendations.push('练习逻辑推理和模式识别题目，提升思维技能');
  }
  if (mathScore < 7) {
    recommendations.push('巩固数学基础概念，加强应用题解决能力');
  }
  
  // Detailed dimension recommendations
  if (detailedScores.reading.criticalAnalysis < 6) {
    recommendations.push('培养批判性思维，学会分析文本观点和论据');
  }
  if (detailedScores.thinking.spatialReasoning < 6) {
    recommendations.push('多练习3D图形和空间变换题目');
  }
  if (detailedScores.mathematics.problemSolving < 6) {
    recommendations.push('重点练习多步骤应用题，提升问题分析能力');
  }
  
  // Positive reinforcement for strong areas
  const strongAreas: string[] = [];
  if (readingScore >= 8) strongAreas.push('阅读理解');
  if (thinkingScore >= 8) strongAreas.push('思维技能');
  if (mathScore >= 8) strongAreas.push('数学推理');
  
  if (strongAreas.length > 0) {
    recommendations.push(`继续发挥${strongAreas.join('、')}方面的优势`);
  }
  
  return recommendations;
};

// Sample data for testing
export const SAMPLE_ASSESSMENT_DATA: OCAssessmentResult = {
  studentId: 'student_001',
  studentName: 'Emma Chen',
  assessmentDate: new Date(),
  reading: {
    literalComprehension: 8.5,
    inferentialUnderstanding: 7.2,
    criticalAnalysis: 6.8,
    textStructureLanguage: 7.5,
    synthesisConnection: 7.0,
    readingStrategies: 8.0,
    overallScore: 7.5
  },
  thinking: {
    patternRecognition: 9.0,
    logicalReasoning: 8.2,
    spatialReasoning: 6.5,
    abstractThinking: 7.8,
    problemSolving: 7.5,
    creativeThinking: 6.9,
    overallScore: 7.7
  },
  mathematics: {
    numberAlgebra: 8.8,
    measurementGeometry: 7.3,
    statisticsProbability: 6.2,
    problemSolving: 7.9,
    mathematicalReasoning: 8.1,
    communication: 6.8,
    overallScore: 7.5
  },
  overall: {
    totalScore: 7.6,
    ocReadiness: 'Good',
    recommendations: [
      '加强批判性阅读分析能力',
      '提升空间推理技能',
      '强化统计与概率概念理解',
      '继续发挥数学运算和模式识别优势'
    ]
  }
};